import React, { type ReactElement, useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useNavigate } from 'react-router'
import { CircularProgress, IconButton, InputAdornment, Typography } from '@mui/material'
import { object, string, type InferType } from 'yup'
import { useFormik } from 'formik'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { ReactComponent as EyeCrossed } from 'assets/eyeCrossed.svg'
import backgroundGraphics from 'assets/backgroundGraphics.svg'
import { ReactComponent as Favicon } from 'assets/favicon.svg'
import LabeledTextfield from 'common/ui/labeled-textfield'
import { ReactComponent as Eye } from 'assets/eye.svg'
import Message from 'common/authentication/Message'
import { StoreStatus } from 'common/utils/types'
import ThemeButton from 'common/ui/theme-button'

import { signIn } from '../auth.service'
import styles from '../reset-password/ResetPassword.module.scss'

const loginSchema = object({
  email: string().email('Invalidan email').required('Obavezno polje').default(''),
  password: string().trim().required('Obavezno polje').default(''),
})

export type LoginForm = InferType<typeof loginSchema>

const Login: React.FC = (): ReactElement => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { status, message, isAuthenticated, user } = useAppSelector((state) => state.auth)
  const [showPassword, setShowPassword] = useState<boolean>(false)

  const defaultValues: LoginForm = {
    email: searchParams.get('email') ?? '',
    password: searchParams.get('password') ?? '',
  }

  const formik = useFormik({
    initialValues: defaultValues,
    validationSchema: loginSchema,
    onSubmit: async (values: LoginForm) => await dispatch(signIn(values)),
  })

  useEffect(() => {
    if (defaultValues.email && defaultValues.password) {
      void dispatch(signIn(defaultValues))
    }
  }, [])

  useEffect(() => {
    if (user && user.challengeName === 'NEW_PASSWORD_REQUIRED') {
      navigate('/auth/confirm', { state: { email: formik.values?.email } })
      return
    }
    if (isAuthenticated) {
      navigate('/')
    }
  }, [formik.values?.email, message, navigate, status, isAuthenticated])

  return (
    <div
      className={styles.container}
      style={{
        backgroundImage: `url(${backgroundGraphics})`,
        backgroundPosition: 'left',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'auto 100%',
      }}
    >
      <div className={styles.textContainer}>
        <p className={styles.text}>
          Dobrodošli na <b>CDT Vendors</b> platformu
        </p>
      </div>
      <form onSubmit={formik.handleSubmit} className={styles.cardBig}>
        <Message />
        <div className={styles.filterCard}>
          <div className={styles.header}>
            <Favicon style={{ height: '20px', width: '20px' }} />
            <Typography variant="title1" sx={{ lineHeight: '22px' }}>
              Prijavite se
            </Typography>
          </div>
          <div className={styles.border} />
          <div className={styles.filtersContainer}>
            <LabeledTextfield
              label="*Email"
              variant="outlined"
              type="text"
              name="email"
              id="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && !!formik.errors.email}
              helperText={formik.touched.email && formik.errors.email}
            />
            <LabeledTextfield
              label="*Lozinka"
              variant="outlined"
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && !!formik.errors.password}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                classes: {
                  input: styles.passwordField,
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={(e) => {
                        e.preventDefault()
                        setShowPassword((show) => !show)
                        document.getElementById('password')?.focus()
                      }}
                      edge="end"
                    >
                      {showPassword ? <Eye /> : <EyeCrossed />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
            <Link to="/auth/forgot-password">
              <Typography variant="title3">Zaboravili ste svoju lozinku?</Typography>
            </Link>
          </div>
          <div className={styles.border} />
          <div className={styles.footer}>
            <ThemeButton
              onClick={() => {
                void formik.submitForm()
              }}
              type="submit"
              disabled={!formik.isValid || !formik.dirty || status === StoreStatus.Loading}
              startIcon={
                status === StoreStatus.Loading ? <CircularProgress size={16} color="primary" /> : null
              }
            >
              Prijavite se
            </ThemeButton>
            <Link to="/auth/registracija">
              <Typography variant="title3">Nemate nalog? Registrujte se ovde</Typography>
            </Link>
          </div>
        </div>
      </form>
    </div>
  )
}

export default Login
